import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { DueItem, TicketFilterAnfrage, TicketKarte, TicketSpalte, Unternehmen } from "@kundeninfo/api-client";
import { Chip, Hinweis, Knopf, Optionen, Tabelle, Werkzeuggruppe, Werkzeugleiste } from "@kundeninfo/ui/me-design";
import { useSofortPopup } from "@kundeninfo/ui";
import { KiKnopf, Suchfeld } from "../../components/meHuellen.js";
import { hausfarbeChip } from "../../markenfarben.js";
import { berlinerTag, datumsMarke, freieTage, tageZwischen } from "./ticketFormat.js";
import { Avatar } from "./Avatar.js";
import { TeamChip } from "./TeamChip.js";
import { TicketFilterleiste, anzahlAktiverFilter, filterAnfrage } from "./TicketFilterleiste.js";
import { HIERARCHY_TEXT } from "./hierarchyTexts.js";
import { EffortMark } from "./EffortMark.js";
import { hasEffort, shownEffort } from "./effort.js";
import { EFFORT_TEXT } from "./effortTexts.js";
import { assigneePopup, dueMark, numberPopup } from "./hierarchy.js";
import type { DueMark } from "./hierarchy.js";
import { LEERER_FILTER, leseFilter, merkeFilter } from "./boardSpeicher.js";
import type { BoardFilter } from "./boardSpeicher.js";
import type { TicketsClient } from "./ticketsClient.js";
import { FremdAbschnitt } from "./FremdAbschnitt.js";
import { SYMBOL_PLUS } from "./ticketSymbole.js";
import type { FremdticketsClient } from "../fremd/fremdClient.js";
import { DueSourceChips } from "../termine/DueSourceChips.js";
import { DueItemsSection } from "../termine/DueItemsSection.js";
import { chipSources, requestSources } from "../termine/dueItems.js";
import type { DueSource } from "../termine/dueItems.js";
import "./Tickets.css";

/**
 * DIE LISTE (Spec „Liste“): `Tabelle stapeln` (am Telefon gestapelt), Ordnung vom Server
 * (Schlechtestes oben, Erledigte am Ende), die Filter des Boards (Ruling A4, dasselbe Gedächtnis
 * `boardSpeicher.ts` — oder von außen über `filter`/`onFilter`), Suche über Nummer und Titel,
 * „Archiv einbeziehen“ (120) — seit 1d-B alle in der Werkzeugleiste „Werkzeuge der Liste“ (204, F129:
 * Gruppen, „Zurücksetzen“ und Telefonblatt macht me-werkzeugleiste), „+ Aufgabe“ als Hauptaktion
 * (`onAnlegen`), 100 je Seite mit „Weitere laden“; das Rollfeld bleibt beim
 * Nachladen dasselbe Element (Scroll bleibt, Hausregel). In der Akte (`kundeId`, Spec
 * „Kundenakte: Reiter ‚Tickets‘“) die Tickets des Kunden ohne Seiten, Filter und Suche, kompakt in
 * fünf Spalten: ohne „Kunden“ (immer derselbe), „Alter“ und „Verknüpfungen“, das Unternehmen als
 * Chip vor der Nummer (Review 19).
 * Titel sind reiner Text (React escaped, Review-Fokus 3) und stehen als Verweis in Textfarbe links
 * (Sichtprobe 9). Die Titelspalte hat eine Mindestbreite: In der schmalen Mittelspalte der Akte
 * rollt die Tabelle dann waagerecht in ihrem Rahmen, statt den Titel Zeichen für Zeichen
 * umzubrechen (Sichtprobe 1); nur die breite Liste bricht lange Wörter überall um.
 * Die breite Liste rechnet ihre Spalten fest (Sichtprobe 1d-C, Mittel 2): Sie passt in ihren
 * Rahmen, Kunden und Spalte kürzen mit Auslassung und nennen den vollen Wert im Sofort-Popup, den
 * Zuständigen kürzt `me-avatar` selbst (Steckbrief im Popup); erst sehr schmal rollt die Tabelle.
 * Teilaufgaben (Spec R11, R15, R28): Die Nummer ist die Anzeige-Nummer („BS-T-42.2“, das Sofort-Popup nennt die echte),
 * der Fortschritt „1/2“ steht mit dem Team-Chip unter dem Titel (Abschluss-Fix, Sichtprobe), „Fällig bis“ hat eine
 * eigene Spalte — die Akte hat keine sechste (Festlegung 11), dort steht es als Kennzeichen unter der Wiedervorlage; das
 * Sofort-Popup der Zuständig-Zelle nennt den Eigentümer (Festlegung 23).
 * Terminebene (Spec D22, D25; Plan T17, T18): mit `dueSources`/`onDueSources` die Zeile „Quellen“ (Chips ohne „Tickets“,
 * Lieferantenquellen nur mit `supplierView`) direkt unter der Werkzeugleiste, mit `onOpenDue` und mindestens einer
 * zugeschalteten Quelle der Abschnitt „Termine“ im Rollfeld vor den Fremdtickets — mit der Suche der Liste. Die
 * Tickettabelle bleibt unverändert; in der Akte gibt es beides nicht.
 */

export type TicketListeProps = {
  client: TicketsClient; companies: readonly Unternehmen[]; onOeffnen: (id: string) => void;
  kundeId?: string; neuLaden?: number; ichId?: string;
  /** Filter von außen (Board und Liste im Bereich teilen ihn, Aufgabe 17); ohne ihn der gemerkte. */
  filter?: BoardFilter;
  /** Bekommt die Änderung als Funktion auf den jüngsten Stand (wie ein `setState`-Updater). */
  onFilter?: (aendern: (alt: BoardFilter) => BoardFilter) => void;
  /** „+ Aufgabe“ in der Werkzeugleiste (204); ohne den Rückruf gibt es den Knopf nicht. */
  onAnlegen?: () => void;
  /** Nachtrag 1f (F143): Abschnitt „Fremdtickets" über der Tabelle; ohne Klient keiner (Tests, Akte). */
  fremd?: FremdticketsClient;
  ichIstAdmin?: boolean;
  /** Name des Angemeldeten — „Zuständig" im Dialog „Fälligkeit …" wie im Board (Review 9, N1). */
  ichName?: string;
  /** Terminebene (D22): die gemerkte Quellenwahl des Bereichs und ihre Änderung — ohne beides keine Zeile „Quellen“. */
  dueSources?: readonly DueSource[];
  onDueSources?: (s: DueSource[]) => void;
  /** Klick auf Nummer oder Titel eines Termins (D25) — ohne ihn kein Abschnitt „Termine“. */
  onOpenDue?: (item: DueItem) => void;
  /** „+ Meilenstein“ im Kopf des Abschnitts „Termine“ (Aufgabe 8) — der Bereich öffnet das Panel. */
  onCreateMilestone?: () => void;
  /** „Ticket anlegen“ an einem Termin (Aufgabe 12). */
  onCreateTicket?: (item: DueItem) => void;
  /** Lieferantensicht (496): nur dann die Chips „Zahlungen“ und „Kündigungsfristen“. */
  supplierView?: boolean;
};

/** Jede Kennung einmal, in der Reihenfolge des ersten Auftretens. */
function ohneDoppelte(zeilen: readonly TicketKarte[]): TicketKarte[] {
  const gesehen = new Set<string>();
  return zeilen.filter((t) => (gesehen.has(t.id) ? false : (gesehen.add(t.id), true)));
}

/** Gekürzter Text — einzeilig (`.tl__kurz`) oder bis zwei Zeilen (`.tl__zweizeilig`), der volle Wert im Sofort-Popup
 *  (Hausregel statt `title`). */
function Gekuerzt(p: { text: string; className: string; zweizeilig?: boolean }): ReactElement {
  const popup = useSofortPopup<HTMLSpanElement>(p.text);
  const art = p.zweizeilig === true ? "tl__zweizeilig" : "tl__kurz";
  return <span ref={popup} className={`${p.className} ${art}`}>{p.text}</span>;
}

/**
 * R11, R28: die Anzeige-Nummer, das Sofort-Popup nennt die echte. Die Wurzel bricht nie um; hinter jedem „.“ der
 * Positionen darf die Zelle umbrechen (`<wbr>`), statt über die feste Spaltenbreite in den Titel zu laufen. In der Akte
 * (`umbrechen` aus, Tabelle ohne feste Spalten) steht die ganze Nummer in einem Stück.
 */
function Nummer(p: { t: TicketKarte; umbrechen: boolean }): ReactElement {
  const popup = useSofortPopup<HTMLSpanElement>(numberPopup(p.t));
  const teile = p.t.displayNumber.split(".");
  const wurzel = p.umbrechen ? Math.max(1, teile.length - p.t.depth) : teile.length;
  return (
    <span ref={popup}>
      <span className="tl__nummerwurzel">{teile.slice(0, wurzel).join(".")}</span>
      {teile.slice(wurzel).map((x, i) => <Fragment key={i}>.<wbr />{x}</Fragment>)}
    </span>
  );
}
/** R28, Festlegung 23: die Zuständig-Zelle trägt ein eigenes Sofort-Popup mit Person UND Rolle des Eigentümers; der
 *  Kreis schweigt (`ohneTipp`), damit das Popup auch über ihm erscheint — in der Akte ist er das Einzige in der Zelle. */
function Zustaendig(p: { t: TicketKarte; ichId: string | undefined; akte: boolean }): ReactElement {
  const popup = useSofortPopup<HTMLSpanElement>(assigneePopup(p.t));
  return (
    <span ref={popup} className="tl__zustaendig">
      <Avatar person={p.t.assignee} ich={p.t.assignee !== null && p.t.assignee.id === p.ichId} ohneTipp
        {...(p.akte ? { ohneName: true } : {})} />
    </span>
  );
}
/** R15: „Fällig bis“ als Kennzeichen — Fehlerton überschritten, Warnton heute, sonst neutral. */
function Faellig(p: { m: DueMark }): ReactElement {
  return p.m.art === null ? <Chip className="tl__faellig">{p.m.text}</Chip>
    : <Chip className="tl__faellig" statusArt={p.m.art}>{p.m.text}</Chip>;
}

export function TicketListe(p: TicketListeProps): ReactElement {
  const [items, setItems] = useState<TicketKarte[] | null>(null);
  // Gibt es hinter der letzten geladenen Seite noch eine? Nach Seiten gerechnet, nicht nach
  // Zeilen — die sind ohne Doppelte womöglich weniger als `total`.
  const [mehr, setMehr] = useState(false);
  const [seite, setSeite] = useState(1);
  const [archiv, setArchiv] = useState(false);
  const [q, setQ] = useState("");
  const [spalten, setSpalten] = useState<TicketSpalte[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [eigenerFilter, setEigenerFilter] = useState<BoardFilter>(() => leseFilter(p.companies.map((c) => c.id)));
  const filter = p.filter ?? eigenerFilter;
  const akte = p.kundeId !== undefined;
  const ich = useMemo(() => (p.ichId === undefined ? null : { id: p.ichId, name: "Ich" }), [p.ichId]);

  // Die Anfrage aus den vier Filtern, stabil über ihre Werte (ein von außen neu gebautes, gleiches
  // Filterobjekt lädt nicht neu).
  const { company, customer, assignee, linked } = filter;
  const team = filter.team ?? "";
  const anfrage: TicketFilterAnfrage = useMemo(() => filterAnfrage({ company, customer, assignee, linked, team }),
    [company, customer, assignee, linked, team]);

  useEffect(() => {
    let aktiv = true;
    p.client.listTicketColumns().then((r) => { if (aktiv) setSpalten(r.items); }).catch(() => undefined);
    return () => { aktiv = false; };
  }, [p.client]);

  // Nur die jüngste Anfrage zeichnet (wie im Board): Tippen in der Suche oder ein Filterwechsel
  // darf nicht von einer älteren Antwort überholt werden.
  const lauf = useRef(0);
  // Die Seiten `von` bis `bis` (P3-Nachlese): „Weitere laden" holt eine Seite dazu, ein Neuladen
  // nach einer Änderung alle bis dahin geladenen — sonst schnurrte die Liste auf Seite 1
  // zusammen. Zeilen gibt es je Kennung nur einmal: Verschiebt sich die Ordnung zwischen zwei
  // Seiten, stünde ein Ticket sonst doppelt da.
  const laden = useCallback(async (von: number, bis: number = von) => {
    const nummer = ++lauf.current;
    try {
      if (p.kundeId !== undefined) {
        const r = await p.client.listCustomerTickets(p.kundeId, archiv);
        if (nummer !== lauf.current) return;
        setItems(r.items); setMehr(false); setSeite(1);
      } else {
        const basis: TicketFilterAnfrage = { ...anfrage, ...(archiv ? { archive: true } : {}), ...(q !== "" ? { q } : {}) };
        const seiten = await Promise.all(Array.from({ length: bis - von + 1 }, (_, i) => von + i)
          .map((s) => p.client.listTickets({ ...basis, ...(s > 1 ? { page: s } : {}) })));
        if (nummer !== lauf.current) return;
        const neu = seiten.flatMap((r) => r.items);
        setItems((alt) => ohneDoppelte(von > 1 && alt !== null ? [...alt, ...neu] : neu));
        const letzte = seiten[seiten.length - 1];
        setMehr(letzte !== undefined && letzte.items.length > 0 && bis * letzte.proSeite < letzte.total);
        setSeite(bis);
      }
      setFehler(null);
    } catch {
      if (nummer === lauf.current) setFehler("Die Tickets konnten nicht geladen werden.");
    }
  }, [p.client, p.kundeId, archiv, q, anfrage]);

  // Neue Anfrage (Filter, Suche, Archiv) → Seite 1; nur `neuLaden` geändert → alle geladenen
  // Seiten neu, die Liste behält ihre Länge (und das Rollfeld seine Position).
  const seiteRef = useRef(seite);
  seiteRef.current = seite;
  const vorher = useRef({ laden, neuLaden: p.neuLaden });
  useEffect(() => {
    const nurNeuLaden = vorher.current.laden === laden && vorher.current.neuLaden !== p.neuLaden;
    vorher.current = { laden, neuLaden: p.neuLaden };
    void laden(1, nurNeuLaden ? seiteRef.current : 1);
  }, [laden, p.neuLaden]);

  // Funktionales Update (Review 19): Die Änderung wirkt auf den JÜNGSTEN Stand, nicht auf den des
  // letzten Zeichnens — zwei schnelle Wechsel hintereinander gehen so nicht verloren.
  const filterSetzen = (f: Partial<BoardFilter>) => {
    const aendern = (alt: BoardFilter): BoardFilter => {
      const neu = { ...alt, ...f };
      merkeFilter(neu);
      return neu;
    };
    if (p.onFilter !== undefined) p.onFilter(aendern);
    if (p.filter === undefined) setEigenerFilter(aendern);
  };

  const spaltenName = (id: string) => spalten.find((s) => s.id === id)?.name ?? "";
  // 204, 61l: Die Liste zählt zu den vier geteilten Filtern die Suche und das Archiv; „Zurücksetzen“
  // leert alle sechs (die vier über das Gedächtnis, damit es auch im Board gilt).
  const aktiv = anzahlAktiverFilter(filter) + (q !== "" ? 1 : 0) + (archiv ? 1 : 0);
  const zuruecksetzen = () => {
    filterSetzen(LEERER_FILTER);
    setQ("");
    setArchiv(false);
  };
  // Der Berliner Kalendertag (F13) — nicht der UTC-Tag aus toISOString.
  const heute = berlinerTag(new Date().toISOString());
  const supplierView = p.supplierView === true;

  return (
    <section className={akte ? "tl tl--akte" : "tl"} aria-label="Ticket-Liste" tabIndex={-1}>
      {/* tabIndex -1: Fokus-Ersatz nach dem Schließen des Panels, wenn auch der Reiter keinen nimmt (F129). */}
      {akte ? (
        // In der Akte keine Werkzeugleiste: nur „Archiv einbeziehen“ wie bisher.
        <div className="tl__werkzeuge" role="toolbar" aria-label="Werkzeuge der Liste">
          <Optionen mehrfach beschriftung="Archiv" wert={archiv ? ["ja"] : []}
            onGeaendert={(d) => setArchiv((d.werte ?? []).includes("ja"))}>
            <option value="ja">Archiv einbeziehen</option>
          </Optionen>
        </div>
      ) : (
        <Werkzeugleiste className="tl__leiste" aria-label="Werkzeuge der Liste" aktiv={aktiv}
          onZurueckgesetzt={zuruecksetzen}
          filter={(
            <>
              <TicketFilterleiste client={p.client} companies={p.companies} ich={ich} tickets={items ?? []}
                filter={filter} onFilter={filterSetzen} />
              <Werkzeuggruppe beschriftung="Suche" aktiv={q !== ""}>
                <Suchfeld className="tl__suche" name="Tickets durchsuchen" platzhalter="Suchen …"
                  wert={q} onText={setQ} />
              </Werkzeuggruppe>
              <Werkzeuggruppe beschriftung="Archiv" aktiv={archiv}>
                {/* Kurz „Archiv“, der Name bleibt „Archiv einbeziehen“ (Label-in-Name): Platz für die Teamgruppe
                    (F134; deploy/README.md, Hinweis Safari/Liste aus 1d-C). */}
                <Knopf art="sekundaer" gestalt="pille" gedrueckt={archiv} aria-label="Archiv einbeziehen"
                  onClick={() => setArchiv((a) => !a)}>
                  Archiv
                </Knopf>
              </Werkzeuggruppe>
            </>
          )}
          {...(p.onAnlegen === undefined ? {}
            : { haupt: <Knopf art="primaer" symbol={SYMBOL_PLUS} onClick={p.onAnlegen}>+ Aufgabe</Knopf> })} />
      )}
      {!akte && p.dueSources !== undefined && p.onDueSources !== undefined && (
        <DueSourceChips sources={chipSources("LIST", supplierView)} value={p.dueSources} onChange={p.onDueSources} />
      )}
      {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
      <div className="tl__rollfeld">
        {/* T18: der Abschnitt „Termine“ über der Tabelle, vor den Fremdtickets (die bleiben). */}
        {!akte && p.dueSources !== undefined && p.onOpenDue !== undefined && p.ichId !== undefined && (
          <DueItemsSection client={p.client} userId={p.ichId} sources={requestSources(p.dueSources, "LIST", supplierView)}
            filter={filter} q={q} {...(p.neuLaden === undefined ? {} : { neuLaden: p.neuLaden })} onOpen={p.onOpenDue}
            onOpenTicket={p.onOeffnen} {...(p.onCreateMilestone === undefined ? {} : { onCreateMilestone: p.onCreateMilestone })}
            {...(p.onCreateTicket === undefined ? {} : { onCreateTicket: p.onCreateTicket })} />
        )}
        {!akte && p.fremd !== undefined && p.ichId !== undefined && (
          <FremdAbschnitt client={p.fremd} filter={filter}
            ich={{ id: p.ichId, istAdmin: p.ichIstAdmin === true, ...(p.ichName === undefined ? {} : { name: p.ichName }) }}
            suche={q} {...(p.neuLaden === undefined ? {} : { neuLaden: p.neuLaden })}
            // Tag und Feiertage aus demselben Board-Stand wie im Board (gleiche Filter), damit „+1 Tag" in beiden
            // Ansichten dasselbe Datum liefert (Review 9, N1). Nur beim Öffnen des Dialogs.
            kalender={async () => {
              const b = await p.client.getTicketBoard(filterAnfrage(filter));
              return { heute: b.heute, feiertage: [...freieTage(b.kalender).keys()] };
            }} />
        )}
        {items !== null && items.length === 0 && <p className="tl__leer">Keine Tickets.</p>}
        {items !== null && items.length > 0 && (
          <Tabelle stapeln dicht={akte}>
            <table className="tl__tabelle">
              <thead>
                <tr>
                  {/* Akte kompakt (Review 19): ohne „Kunden“ (immer derselbe), „Alter“ und
                      „Verknüpfungen“ — fünf Spalten passen in die schmale Mittelspalte. */}
                  {/* Die Klassen tragen die festen Spaltenbreiten der breiten Liste (Tickets.css). */}
                  <th scope="col" className="tl__sp-nummer">Nummer</th><th scope="col">Titel</th>
                  {!akte && <th scope="col" className="tl__sp-kunden">Kunden</th>}
                  <th scope="col" className="tl__sp-zustaendig">Zuständig</th>
                  <th scope="col" className="tl__sp-spalte">Spalte</th>
                  <th scope="col" className="tl__sp-wiedervorlage">Wiedervorlage</th>
                  {!akte && <th scope="col" className="tl__sp-faellig">{HIERARCHY_TEXT.dueDate}</th>}
                  {!akte && <th scope="col" className="tl__sp-alter" data-ausrichtung="rechts">Alter</th>}
                  {!akte && <th scope="col" className="tl__sp-aufwand" data-ausrichtung="rechts">{EFFORT_TEXT.section}</th>}
                  {!akte && <th scope="col" className="tl__sp-verknuepfungen">Verknüpfungen</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((t) => {
                  const marke = datumsMarke(t.resubmitDate, heute);
                  const faellig = dueMark(t.dueDate, heute, t.stateKind === "DONE");
                  const farbe = hausfarbeChip(p.companies, t.companyShortCode);
                  return (
                    <tr key={t.id}>
                      <td className="tl__nummer">
                        {/* In der Akte trägt die Nummer das Unternehmen als Chip (die Kundenspalte fehlt). */}
                        {akte && <Chip className="tl__nummerchip" {...farbe}>{t.companyShortCode}</Chip>}
                        <Nummer t={t} umbrechen={!akte} />
                      </td>
                      <td className="tl__titel">
                        {/* Sichtprobe Teilaufgaben: Kennzeichen verdrängen den Titel nie. Der Titel steht zuerst, bricht
                            um, nach zwei Zeilen Auslassung (voll im Sofort-Popup); darunter die Kennzeichen in eigener
                            Zeile — Team (F134) und Fortschritt „1/2“ (R28). EIN Kind in der Zelle (wie `tl__kennzeichen`):
                            gestapelt fiele ein zweites in die Beschriftungsspalte. */}
                        <div className="tl__titelblock">
                          <KiKnopf art="leise" className="tl__titelknopf" onClick={() => p.onOeffnen(t.id)}>
                            <Gekuerzt className="tl__titeltext" text={t.title} zweizeilig />
                          </KiKnopf>
                          {(t.team !== null || t.subtaskTotal > 0) && (
                            <div className="tl__titelchips">
                              <TeamChip team={t.team} className="tl__teamchip" />
                              {t.subtaskTotal > 0 && (
                                <span className="tl__fortschritt" role="img"
                                  aria-label={HIERARCHY_TEXT.progressSpoken(t.subtaskDone, t.subtaskTotal)}>
                                  <Chip>{HIERARCHY_TEXT.progress(t.subtaskDone, t.subtaskTotal)}</Chip>
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      {!akte && <td>{t.customers.length === 0
                        ? <Chip {...farbe}>{t.companyShortCode}</Chip>
                        : t.customers.map((c) => (
                          <Chip key={c.id} {...farbe}>
                            <Gekuerzt className="tl__kundetext" text={`${t.companyShortCode} · ${c.name}`} />
                          </Chip>
                        ))}</td>}
                      {/* In der Akte nur der Kreis (Name im Sofort-Popup und als zugänglicher Name) —
                          sonst passen fünf Spalten nicht in die Mittelspalte (Review 19). */}
                      <td><Zustaendig t={t} ichId={p.ichId} akte={akte} /></td>
                      <td className="tl__spalte"><Gekuerzt className="tl__spaltetext" text={spaltenName(t.columnId)} /></td>
                      {/* EIN Kind in der Zelle (`tl__kennzeichen`): me-tabelle stapelt am Telefon jede Zelle als Raster mit
                          Beschriftung — zwei Kinder fielen sonst in die Beschriftungsspalte. */}
                      <td className="tl__wiedervorlage"><div className="tl__kennzeichen">
                        {t.stateKind === "DONE" ? (t.status === "ARCHIVED" ? "archiviert" : "erledigt")
                          : marke.art === null
                            ? <Chip>{marke.text}</Chip>
                            : <Chip statusArt={marke.art}>{marke.text}</Chip>}
                        {akte && faellig !== null && <Faellig m={faellig} />}
                      </div></td>
                      {!akte && <td>{faellig === null ? HIERARCHY_TEXT.none : <Faellig m={faellig} />}</td>}
                      {!akte && <td data-ausrichtung="rechts">{tageZwischen(berlinerTag(t.createdAt), heute)} T</td>}
                      {!akte && <td data-ausrichtung="rechts" className="tl__aufwand">{hasEffort(shownEffort(t))
                        ? <EffortMark card={t} className="tl__aufwandmark" /> : EFFORT_TEXT.none}</td>}
                      {!akte && <td>{t.linkCount}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Tabelle>
        )}
        {!akte && items !== null && mehr && (
          <KiKnopf onClick={() => { void laden(seite + 1); }}>Weitere laden</KiKnopf>
        )}
      </div>
    </section>
  );
}
